/**
 * Joycelyn Fashion — Circular Macro Magnifier & Minimal Plain Lightbox
 * Touch-optimized for mobile & responsive desktop inspection.
 * Zero extra wording, no frames, plain image display in lightbox.
 */

class CircularMacroMagnifier {
  constructor(container) {
    this.container = container;
    this.img = container.querySelector('img.main-embroidery-img');
    this.lens = container.querySelector('.macro-zoom-lens');
    this.hint = container.querySelector('.zoom-hint-capsule span');
    this.highResSrc = container.getAttribute('data-highres-src') || (this.img ? this.img.src : '');

    this.isMagnifying = false;
    this.zoomFactor = 2.6;
    this.lensRadius = 90;
    this.lastTouchTime = 0;
    this.touchHandled = false;

    if (!this.container || !this.img || !this.lens) return;
    this.init();
    this.initLightbox();
  }

  init() {
    // 1. TOUCH EVENTS FOR MOBILE & TABLET
    this.container.addEventListener('touchstart', (e) => {
      // Ignore if user tapped fullscreen lightbox trigger or control pills
      if (e.target.closest('[data-open-macro-lightbox]') || e.target.closest('.stage-floating-pills')) return;

      if (e.touches && e.touches.length === 1) {
        this.touchHandled = true;
        this.lastTouchTime = Date.now();
        this.isMagnifying = !this.isMagnifying;

        if (this.isMagnifying) {
          this.activate(e.touches[0], true);
        } else {
          this.deactivate();
        }
      }
    }, { passive: true });

    this.container.addEventListener('touchmove', (e) => {
      if (!this.isMagnifying) return;
      if (e.touches && e.touches.length === 1) {
        // Prevent accidental page scroll while actively moving the magnifying lens
        if (e.cancelable) {
          e.preventDefault();
        }
        this.updatePosition(e.touches[0].clientX, e.touches[0].clientY, true);
      }
    }, { passive: false });

    // 2. CLICK EVENTS FOR DESKTOP & MOUSE
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('[data-open-macro-lightbox]') || e.target.closest('.stage-floating-pills')) return;

      // Ignore synthetic click right after a touch tap on mobile
      if (this.touchHandled || (Date.now() - this.lastTouchTime < 600)) {
        this.touchHandled = false;
        return;
      }

      this.isMagnifying = !this.isMagnifying;

      if (this.isMagnifying) {
        this.activate(e, false);
      } else {
        this.deactivate();
      }
    });

    // 3. MOUSE MOVE (Only moves lens when magnifier is ACTIVE)
    this.container.addEventListener('mousemove', (e) => {
      if (!this.isMagnifying) return;
      this.updatePosition(e.clientX, e.clientY, false);
    });

    // 4. MOUSE LEAVE (Dismisses magnifier when cursor exits container on desktop)
    this.container.addEventListener('mouseleave', () => {
      if (this.isMagnifying && (Date.now() - this.lastTouchTime > 1000)) {
        this.deactivate();
      }
    });
  }

  activate(eventOrTouch, isTouch = false) {
    this.container.classList.add('is-magnifying');

    // Retrieve active high-resolution image source in case user selected another thumbnail
    const currentSrc = this.container.getAttribute('data-highres-src') || (this.img ? this.img.src : '');
    this.lens.style.backgroundImage = `url('${currentSrc}')`;

    if (this.hint) {
      this.hint.textContent = isTouch ? 'Tap image to close lens · Drag to inspect' : 'Click to close lens · Move to inspect';
    }

    if (eventOrTouch) {
      this.updatePosition(eventOrTouch.clientX, eventOrTouch.clientY, isTouch);
    }
  }

  deactivate() {
    this.isMagnifying = false;
    this.container.classList.remove('is-magnifying');

    if (this.hint) {
      this.hint.textContent = 'Tap image to magnify fabric';
    }
  }

  updatePosition(clientX, clientY, isTouch = false) {
    const target = this.img || this.container;
    const rect = target.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    // Dynamic lens radius based on computed size (e.g. 140px on mobile, 180px on desktop)
    const lensRadius = (this.lens.offsetWidth || 180) / 2;

    // Center lens or position slightly above touch finger on mobile so finger doesn't block lens
    const lensX = clientX - containerRect.left;
    const lensY = isTouch ? Math.max(lensRadius, clientY - containerRect.top - 36) : (clientY - containerRect.top);

    this.lens.style.left = `${lensX}px`;
    this.lens.style.top = `${lensY}px`;

    // Zoom background calculations
    const bgWidth = rect.width * this.zoomFactor;
    const bgHeight = rect.height * this.zoomFactor;

    const bgX = lensRadius - (x * this.zoomFactor);
    const bgY = lensRadius - (y * this.zoomFactor);

    this.lens.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
    this.lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
  }

  initLightbox() {
    const getTrigger = () => {
      return this.container.querySelector('[data-open-macro-lightbox]') ||
        this.container.closest('.couture-stage-wrapper')?.querySelector('[data-open-macro-lightbox]') ||
        document.querySelector('[data-open-macro-lightbox]');
    };

    const openLightbox = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.deactivate();

      let modal = document.getElementById('macro-lightbox-modal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'macro-lightbox-modal';
        modal.className = 'macro-lightbox-overlay';
        modal.innerHTML = `
          <button type="button" data-close-lightbox class="macro-lightbox-close" aria-label="Close image popup">&times;</button>
          <div class="macro-lightbox-img-wrap">
            <img id="macro-lightbox-img" src="${this.highResSrc || (this.img ? this.img.src : '')}" alt="Product Image" />
          </div>
        `;
        document.body.appendChild(modal);

        modal.addEventListener('click', (ev) => {
          if (ev.target === modal || ev.target.closest('[data-close-lightbox]') || ev.target.classList.contains('macro-lightbox-img-wrap')) {
            modal.classList.remove('is-active');
            document.body.style.overflow = '';
          }
        });
      }

      const modalImg = modal.querySelector('#macro-lightbox-img');
      const currentSrc = this.container.getAttribute('data-highres-src') || (this.img ? this.img.src : '');
      if (modalImg) {
        modalImg.src = currentSrc;
      }
      modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    const lightboxTrigger = getTrigger();
    if (lightboxTrigger) {
      lightboxTrigger.addEventListener('click', openLightbox);
    }
  }
}

function initCircularMacroMagnifiers() {
  document.querySelectorAll('.macro-zoom-container').forEach(c => new CircularMacroMagnifier(c));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCircularMacroMagnifiers);
} else {
  initCircularMacroMagnifiers();
}
